import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";
import { ArrowLeft, User, Lock, Eye, EyeOff } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Dedicated admin login page — reachable from the maintenance overlay via
 * the « connectez-vous » link. Reuses the existing client login endpoint
 * (`loginClient` → POST /api/auth/login/client) but strictly enforces
 * `isAdmin=true`. Any non-admin who successfully logs in is immediately
 * logged out with a clear French error.
 *
 * The page is rendered fullscreen (see `Layout` in App.js) — its own
 * legal footer replaces the global one, matching the approved mockup.
 */
export default function AdminLogin() {
  const { loginClient, logout } = useAuth();
  const [form, setForm] = useState({ email: "", password: "" });
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();

  // When the site is in maintenance mode, any non-admin login attempt on
  // this page (wrong credentials, establishment account, or non-admin
  // visitor) must surface the exact same French message, so no one can
  // deduce whether an account exists or not while maintenance is on.
  const isMaintenanceOn = async () => {
    try {
      const { data } = await axios.get(`${API}/system/maintenance`);
      return !!data?.enabled;
    } catch {
      return false;
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const data = await loginClient(form.email, form.password);
      if (!data?.user?.isAdmin) {
        await logout();
        const maintenance = await isMaintenanceOn();
        toast.error(maintenance ? "Accès impossible. Site en maintenance." : "Accès réservé aux administrateurs.");
        return;
      }
      toast.success("Connecté !");
      nav("/admin");
    } catch (err) {
      const maintenance = await isMaintenanceOn();
      toast.error(
        maintenance
          ? "Accès impossible. Site en maintenance."
          : (err.response?.data?.detail || "Erreur de connexion")
      );
    } finally {
      setLoading(false);
    }
  };

  const year = new Date().getFullYear();

  return (
    <div className="min-h-screen bg-white flex flex-col" data-testid="admin-login-page">
      {/* Hero visual — helmet + tools on blue construction background */}
      <div className="w-full max-w-md mx-auto">
        <img
          src="/admin-login-hero.jpg"
          alt="BypassGo — Console administrateur en mode maintenance"
          className="w-full block"
        />
      </div>

      {/* White card with title, form, and legal footer */}
      <div className="flex-1 -mt-4 rounded-t-3xl bg-white px-6 pt-8 pb-8 max-w-md w-full mx-auto shadow-none">
        <h1 className="text-center font-display text-3xl md:text-4xl font-black tracking-tight text-ink">
          Console administrateur
        </h1>
        <p className="mt-2 text-center text-subtle text-base">
          Accès réservé – Mode maintenance
        </p>
        <div className="mx-auto mt-3 h-1 w-16 bg-[#F5B417] rounded-full" />

        <form onSubmit={submit} className="mt-8 space-y-4" data-testid="admin-login-form">
          {/* Identifiant */}
          <div className="flex items-center gap-3 h-14 px-4 border border-border rounded-xl focus-within:border-[#1D4ED8] transition-colors">
            <User size={20} className="text-ink/60 shrink-0" />
            <input
              type="email"
              required
              autoComplete="username"
              placeholder="Identifiant administrateur"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="flex-1 h-full bg-transparent outline-none text-ink placeholder:text-ink/50 text-base"
              data-testid="admin-email-input"
            />
          </div>

          {/* Mot de passe */}
          <div className="flex items-center gap-3 h-14 px-4 border border-border rounded-xl focus-within:border-[#1D4ED8] transition-colors">
            <Lock size={20} className="text-ink/60 shrink-0" />
            <input
              type={showPwd ? "text" : "password"}
              required
              autoComplete="current-password"
              placeholder="Mot de passe"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              className="flex-1 h-full bg-transparent outline-none text-ink placeholder:text-ink/50 text-base"
              data-testid="admin-password-input"
            />
            <button
              type="button"
              onClick={() => setShowPwd((v) => !v)}
              className="text-ink/60 hover:text-ink shrink-0"
              aria-label={showPwd ? "Masquer le mot de passe" : "Afficher le mot de passe"}
              data-testid="admin-password-toggle"
            >
              {showPwd ? <Eye size={20} /> : <EyeOff size={20} />}
            </button>
          </div>

          {/* Se connecter */}
          <button
            type="submit"
            disabled={loading}
            className="w-full h-14 bg-[#1D4ED8] hover:bg-[#1E40AF] text-white font-semibold rounded-xl inline-flex items-center justify-center gap-2 transition-colors disabled:opacity-70"
            data-testid="admin-submit-btn"
          >
            <Lock size={18} />
            <span>{loading ? "…" : "Se connecter"}</span>
          </button>
        </form>

        {/* Retour maintenance */}
        <div className="mt-6 text-center">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-[#1D4ED8] font-medium hover:underline"
            data-testid="admin-back-maintenance-link"
          >
            <ArrowLeft size={18} />
            Retour à la page de maintenance
          </Link>
        </div>

        {/* Divider + legal footer */}
        <div className="mt-8 border-t border-border pt-6">
          <ul className="flex flex-wrap items-center justify-center gap-x-3 gap-y-2 text-sm text-subtle">
            <li><a href="/contact" className="hover:text-ink">Contact</a></li>
            <li aria-hidden="true" className="text-border">|</li>
            <li><a href="/legal#cgu" className="hover:text-ink">Conditions générales</a></li>
            <li aria-hidden="true" className="text-border">|</li>
            <li><a href="/legal#confidentialite" className="hover:text-ink">Politique de confidentialité</a></li>
            <li aria-hidden="true" className="text-border w-full text-center sm:w-auto">|</li>
            <li><a href="/legal#mentions-legales" className="hover:text-ink">Mentions légales</a></li>
            <li aria-hidden="true" className="text-border">|</li>
            <li><a href="/help" className="hover:text-ink">Aide</a></li>
          </ul>
          <p className="mt-4 text-center text-xs text-subtle">
            © {year} BypassGo. Tous droits réservés.
          </p>
        </div>
      </div>
    </div>
  );
}
